import axios from "axios";
import { useAuthStore } from "@/lib/auth/authStore";
import { usePermissionStore } from "@/lib/stores/permissions.store";

/**
 * Create axios instance with base configuration
 */
const api = axios.create({
  baseURL: "/api",
  timeout: 30000, // 30 second timeout
  headers: {
    Accept: "application/ld+json",
    "Content-Type": "application/ld+json",
  },
});

/**
 * Refresh token lock - prevents multiple simultaneous refresh attempts.
 * Only one request should refresh, others wait for it to complete.
 */
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Add a callback to the queue of requests waiting for token refresh.
 * @param {Function} callback - Function to call with the new token
 * @returns {Function} Cleanup function to remove this subscriber
 */
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
  return () => {
    refreshSubscribers = refreshSubscribers.filter((cb) => cb !== callback);
  };
};

/**
 * Notify all waiting subscribers that a new token is available.
 * @param {string} token - The new access token
 */
const onRefreshComplete = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Request interceptor for adding auth token and setting proper headers
 */
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || new axios.AxiosHeaders();

    // Add authentication token
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // API Platform requires application/ld+json for POST and GET
    if (config.method === "post") {
      config.headers["Content-Type"] = "application/ld+json";
    } else if (config.method === "patch") {
      config.headers["Content-Type"] = "application/merge-patch+json";
    } else if (config.method === "get") {
      // Ensure Accept is set for GET requests
      if (!config.headers.get("Accept")) {
        config.headers.setAccept("application/ld+json");
      }
    }

    return config;
  },
  (error) => {
    // Handle request errors
    console.error("Request error:", error);
    return Promise.reject(error);
  },
);

/**
 * Response interceptor for error handling and token refresh
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      console.error("Network error - please check your connection");
      return Promise.reject({
        ...error,
        message: "Erreur réseau - Vérifiez votre connexion internet",
      });
    }

    const { status } = error.response;

    // Handle 401 Unauthorized - with retry guard
    if (status === 401 && !originalRequest._retry) {
      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;

      // If already refreshing, wait for the new token
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken) => {
            if (newToken) {
              // Use the new token for this request
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            } else {
              // Refresh failed, reject this request too
              reject(error);
            }
          });
        });
      }

      // Get refresh token before setting isRefreshing flag
      const refreshToken = useAuthStore.getState().refreshToken;

      // No refresh token available - force logout
      if (!refreshToken) {
        console.warn("[TokenRefresh] No refresh token available, logging out");
        useAuthStore.getState().logout();
        usePermissionStore.getState().clear();
        if (typeof window !== "undefined") {
          window.location.href = "/auth/login";
        }
        return Promise.reject(error);
      }

      // Mark as refreshing
      isRefreshing = true;

      try {
        const response = await axios.post(
          "/api/token/refresh",
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );
        const { token, refresh_token } = response.data;

        // Update auth store FIRST with new tokens
        useAuthStore.getState().login(token, refresh_token);

        // Notify all waiting subscribers BEFORE retrying request
        onRefreshComplete(token);
        isRefreshing = false;

        // Apply new token to the original request and retry
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("[TokenRefresh] Failed", {
          status: refreshError.response?.status,
          data: refreshError.response?.data,
        });
        // Clean up state on failure
        onRefreshComplete(null);
        isRefreshing = false;

        // Force logout on refresh failure
        useAuthStore.getState().logout();
        usePermissionStore.getState().clear();

        if (typeof window !== "undefined") {
          window.location.href = "/auth/login";
        }
        return Promise.reject(error);
      }
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.error("Access forbidden - insufficient permissions");
      return Promise.reject({
        ...error,
        message: "Accès interdit - Permissions insuffisantes",
      });
    }

    // Handle 404 Not Found
    if (status === 404) {
      console.error("Resource not found");
      return Promise.reject({
        ...error,
        message: "Ressource non trouvée",
      });
    }

    // Handle 422 Unprocessable Entity (validation errors)
    if (status === 422) {
      console.error("Validation error:", error.response.data);
      return Promise.reject({
        ...error,
        message: "Erreur de validation",
        validationErrors: error.response.data?.violations || [],
      });
    }

    // Handle 500 Server Error
    if (status >= 500) {
      console.error("Server error:", error);
      return Promise.reject({
        ...error,
        message: "Erreur serveur - Veuillez réessayer plus tard",
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Fetches permissions for a specific user.
 * @param {number|string} userId - The ID of the user.
 * @returns {Promise<object>} - The user's permissions object.
 */
export const fetchUserPermissions = async (userId) => {
  if (!userId) {
    throw new Error("User ID is required to fetch permissions.");
  }

  try {
    const response = await api.get(`/users/${userId}/permissions`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch permissions for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Refreshes permissions for the current user and updates the store.
 * This is meant to be called from a query invalidation context.
 * @returns {Promise<object|null>} - The permissions object or null if no user.
 */
export const refreshPermissions = async () => {
  try {
    const currentUser = useAuthStore.getState().user;
    if (!currentUser?.id) {
      console.warn("Cannot refresh permissions: No authenticated user");
      return null;
    }

    const permissions = await fetchUserPermissions(currentUser.id);
    usePermissionStore.getState().setPermissions(permissions);
    return permissions;
  } catch (error) {
    console.error("Failed to refresh permissions:", error);
    throw error;
  }
};

/**
 * Helper to extract numeric ID from API Platform IRI
 * @param {string} iri - The IRI (e.g., "/api/civilites/40")
 * @returns {number|null} - The numeric ID or null if invalid
 */
export const extractIdFromIri = (iri) => {
  if (!iri || typeof iri !== "string") return null;
  const parts = iri.split("/");
  const id = parseInt(parts[parts.length - 1], 10);
  return Number.isNaN(id) ? null : id;
};

/**
 * Normalize API Platform collection data to consistent format
 * - Extracts numeric id from @id IRI and adds it as 'id' property
 * - Preserves all original properties (@id, @type, code, libelle, etc.)
 * @param {Array|Object} data - API Platform response (with member) or array
 * @returns {Array} - Normalized array with numeric 'id' property
 */
export const normalizeApiPlatformCollection = (data) => {
  const member = data?.member || data || [];
  if (!Array.isArray(member)) {
    return [];
  }
  return member.map((item) => ({
    ...item,
    id: item.id ?? extractIdFromIri(item["@id"]),
  }));
};

/**
 * Helper to create an IRI from resource type and ID
 * @param {string} resourceType - The resource type (e.g., "civilites")
 * @param {number} id - The numeric ID
 * @returns {string} - The IRI (e.g., "/api/civilites/40")
 */
export const createIri = (resourceType, id) => {
  return `/api/${resourceType}/${id}`;
};

/**
 * Helper to transform data for API Platform submission
 * Converts numeric IDs to IRIs for relations
 * @param {object} data - The form data
 * @param {object} relations - Map of field names to resource types
 * @returns {object} - Transformed data ready for API Platform
 */
export const transformForApiPlatform = (data, relations = {}) => {
  const transformed = { ...data };

  // Convert numeric IDs to IRIs for specified relations
  Object.entries(relations).forEach(([field, resourceType]) => {
    if (transformed[field] !== null && transformed[field] !== undefined) {
      transformed[field] = createIri(resourceType, transformed[field]);
    }
  });

  return transformed;
};

export default api;
